import { Input, Label } from '@bpinternal/ui-kit-next'
import { AnimatePresence, motion } from 'framer-motion'
import { Pencil } from 'lucide-react'
import { useMemo, useState } from 'react'
import useMeasure from 'react-use-measure'
import { cn } from '~/utils'

type ValidateResult = {
  isValid: boolean
  errorMessage?: string
}

type PageTitleInputProps = {
  defaultName: string
  placeholder?: string
  maxLength?: number
  onRename: (name: string) => void
  onValidate?: (name: string) => ValidateResult
  isPending?: boolean
  isSuccess?: boolean
  showError?: boolean
  className?: string
  inputClassName?: string
  displayNameClassName?: string
  errorClassName?: string
}

export const PageTitleInput = ({
  defaultName,
  maxLength,
  onRename,
  onValidate: validate,
  isPending,
  isSuccess,
  placeholder,
  showError = false,
  className,
  inputClassName,
  displayNameClassName,
  errorClassName,
}: PageTitleInputProps) => {
  const [name, setName] = useState(defaultName)
  const [isEditing, setIsEditing] = useState(false)
  const [validationError, setValidationError] = useState('')
  const showErrorState = useMemo(() => showError && validationError, [showError, validationError])
  const MotionLabel = motion(Label)

  const [spanRef, { width }] = useMeasure()

  const handleRename = () => {
    if (name.length === 0) {
      setName(defaultName)
    } else if (name !== defaultName) {
      onRename(name)
    }
    setValidationError('')
    setIsEditing(false)
  }

  const handleChange = (newName: string) => {
    if (newName === name) {
      return
    }
    if (maxLength && newName.length > maxLength) {
      setValidationError(`Name must be ${maxLength} characters or less`)
    } else if (validate) {
      const result = validate(newName)
      if (!result.isValid) {
        setValidationError(result.errorMessage ?? 'Invalid name')
      }
    } else {
      setValidationError('')
      setName(newName)
    }
  }

  return (
    <div className={cn('group flex h-8 min-w-0 flex-col gap-4', className)}>
      {/* This is a hack to get the actual width of the text for auto-sizing the input width.
      The padding is to keep the width changes smooth */}
      <span ref={spanRef} aria-hidden="true" className="invisible absolute left-0 px-[0.6ch] text-2xl">
        {name}
      </span>
      <AnimatePresence mode="popLayout" initial={true}>
        {isEditing ? (
          <motion.div
            key="editing"
            className="flex w-full flex-col gap-2"
            initial={{ opacity: 0, filter: 'blur(3px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, filter: 'blur(3px)' }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex w-full items-center gap-4">
              <Input
                value={name}
                ref={(el) => el?.focus()}
                onChange={(e) => {
                  handleChange(e.target.value)
                }}
                onBlur={handleRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRename()
                  }
                }}
                placeholder={placeholder}
                style={{ width: width }}
                className={cn(
                  'm-0 h-auto min-w-64 rounded-none border-x-0 border-b border-t-0 p-0 !text-2xl ring-0 focus-visible:ring-0',
                  showErrorState && 'focus-visible:border-danger-500 focus-visible:text-danger-500',
                  inputClassName
                )}
              />
              <Pencil size={24} className={cn('flex-none', showErrorState && validationError && 'text-danger-500')} />
            </div>
            <AnimatePresence mode="wait">
              {maxLength && maxLength > 0 && !validationError ? (
                <MotionLabel
                  key="maxLength"
                  className="text-xs text-muted-foreground"
                >{`${name.length}/${maxLength} characters`}</MotionLabel>
              ) : (
                showErrorState && (
                  <MotionLabel
                    key="validationError"
                    initial={{ y: '-100%', filter: 'blur(1px)' }}
                    animate={{ y: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    exit={{ y: '-100%', filter: 'blur(1px)' }}
                    className={cn('h-4 w-fit text-xs text-danger-500', errorClassName)}
                  >
                    {validationError}
                  </MotionLabel>
                )
              )}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            key="viewing"
            className="flex w-full items-center gap-4 text-2xl"
            initial={{ opacity: 0, filter: 'blur(3px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <p
              className={cn(
                'cursor-pointer truncate border-b border-transparent font-semibold transition-colors duration-200 ease-out',
                isPending ? 'text-neutral-400' : 'group-hover:border-neutral-500',
                displayNameClassName
              )}
              onClick={() => {
                setIsEditing(true)
              }}
            >
              {isPending || isSuccess ? name : defaultName}
            </p>
            {!isPending && (
              <Pencil
                onClick={() => {
                  setIsEditing(true)
                }}
                size={20}
                className="min-w-5 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
